/**
 * LA RÉTROACTION — un format unique pour le retour de TOUT travail de l'élève
 * (chantier « Rétroaction », P1 ; vision : `docs/pilotage/retour-annote/v2/RETROACTION.html`).
 *
 * Le modèle écrit des DONNÉES, jamais la page. Cette forme est ASSEMBLÉE par le
 * serveur — la sortie validée de l'appel de correction EXISTANT, le manuel
 * (pièges, fiches, capsules, exemples) et le code (unités, verdict, liens,
 * récurrence, ordre du plan) — puis stockée telle quelle et rendue par un seul
 * jeu de règles : l'écran natif de l'élève, et un gabarit HTML unique pour le
 * PDF, le parent et le tuteur (décision « mixte » du fondateur, 15 oct. 2026).
 *
 * Qui écrit quoi (`provenance`) :
 *  - `code`   : unites, mesures, verdict, constat principal, liens, récurrence,
 *               leçon, fil, ordre du plan ;
 *  - `manuel` : messages des codes au catalogue, pièges, exemples, capsules ;
 *  - `modele_correction` : extraits, constats hors catalogue, réussites et
 *               résumé quand l'appel les écrit (P2), niveaux du français (P3) ;
 *  - `modele_demande` : ce qu'un geste « à la demande » a fait écrire (P4).
 *
 * Le français est prévu dès maintenant (niveaux idées / cohérence / langue,
 * ancrage à la phrase, réécritures) même s'il n'est servi qu'en P3.
 */
import { z } from 'zod';
export const VERSION_RETROACTION = 'retro-1';
export const TYPES_TRAVAIL_RETROACTION = ['exercice', 'situation', 'copie', 'texte'];
export const SOURCES_RETROACTION = [
    'manuel',
    'code',
    'modele_correction',
    'modele_demande',
];
export const GENRES_UNITE = ['ligne', 'phrase', 'question'];
/** Ce qu'un bloc lu EST — la cause du « nom lu comme un exercice » (P1). */
export const BLOCS_UNITE = ['travail', 'identite', 'consigne', 'brouillon'];
export const NATURES_CONSTAT = ['erreur', 'suite', 'conseil', 'bravo'];
export const ETATS_COMPETENCE = ['reussi', 'partiel', 'a_revoir', 'non_observe'];
export const CODES_COMPETENCE = [
    // mathématiques
    'comprendre',
    'modeliser',
    'calculer',
    'valider',
    'communiquer',
    // français : les trois niveaux montrés à l'élève
    'idees',
    'coherence',
    'langue',
];
export const CATEGORIES_CONSTAT = [
    'accord',
    'syntaxe',
    'ponctuation',
    'vocabulaire',
    'orthographe',
    'registre',
    'discours_rapporte',
    'argumentation',
    'calcul',
    'modelisation',
    'unites',
    'validation',
];
/** Le code d'un constat que le catalogue ne connaît pas (vérifié contre `codesPermis`). */
export const CODE_HORS_CATALOGUE = '__hors_catalogue__';
const texte = z.string().min(1);
const source = z.enum(SOURCES_RETROACTION);
const verdict = z.enum(['juste', 'partiel', 'erreur']);
/** Où mène un geste : une route de l'app, jamais une URL arbitraire. */
export const lienSchema = z
    .object({
    libelle: texte,
    vers: z.enum(['fiche', 'capsule', 'exercice', 'notion', 'reecrire', 'copie']),
    code: texte,
    ancre: z.string().optional(),
})
    .strict();
export const uniteSchema = z
    .object({
    id: texte,
    genre: z.enum(GENRES_UNITE),
    parent: z.string().optional(),
    texte: z.string(),
    tex: z.string().optional(),
    /** Une zone mal transcrite n'est JAMAIS comptée en orthographe (spec § 7.5). */
    confiance: z.enum(['sure', 'incertaine']).optional(),
    bloc: z.enum(BLOCS_UNITE).optional(),
    /** Exercice inféré par la lecture et non confirmé : hors du dénominateur. */
    infere: z.boolean().optional(),
})
    .strict();
export const ancreSchema = z.union([
    z.object({ unite: texte, extrait: z.string().optional() }).strict(),
    /** Ce qui MANQUE (« la validation est absente »), après une unité. */
    z.object({ manque: texte, apres: z.string().optional() }).strict(),
]);
export const liensConstatSchema = z
    .object({
    fiche: z.object({ code: texte, ancre: texte }).strict().optional(),
    capsule: z
        .object({
        code: texte,
        /** Reporté (P4) : absents tant que les scènes ne sont pas étiquetées. */
        debutMs: z.number().int().nonnegative().optional(),
        finMs: z.number().int().nonnegative().optional(),
    })
        .strict()
        .optional(),
    exemple: z
        .object({
        source: z.enum(['fiche', 'erreur', 'cache', 'a_generer']),
        ref: z.string().optional(),
    })
        .strict()
        .optional(),
    exerciceSemblable: z.string().optional(),
})
    .strict();
export const constatSchema = z
    .object({
    id: texte,
    nature: z.enum(NATURES_CONSTAT),
    /** UN constat principal au plus — « la chose à retravailler ». Choisi par le CODE. */
    principal: z.boolean().optional(),
    ancre: ancreSchema,
    competence: z.enum(CODES_COMPETENCE).optional(),
    niveau: z.string().optional(),
    categorie: z.enum(CATEGORIES_CONSTAT).optional(),
    code: texte,
    /** Le `messageEleve` du catalogue si le code y est ; sinon ce que la correction a écrit. */
    message: texte,
    /** SEULEMENT hors catalogue (P2). */
    explication: z.object({ cloche: texte, regle: texte }).strict().optional(),
    correction: z.object({ avant: z.string(), apres: z.string() }).strict().optional(),
    /** Français (P3) : formes acceptées d'une réécriture, comparées sans modèle. */
    reecritures: z.array(texte).max(5).optional(),
    presque: z
        .array(z.object({ forme: texte, indice: texte }).strict())
        .max(3)
        .optional(),
    /**
     * Français (P3-d) : les AUTRES passages où la même erreur revient (« a dit »,
     * quatre fois). Le constat est ancré sur la première ; chaque répétition
     * l'est sur sa phrase, extrait vérifié par le code comme celui de l'ancre.
     */
    repetitions: z
        .array(z.object({ unite: texte, extrait: z.string().optional() }).strict())
        .max(8)
        .optional(),
    /** Résolus par le SERVEUR, jamais par le modèle. */
    liens: liensConstatSchema.optional(),
    recurrence: z
        .object({ fois: z.number().int().positive(), fenetreJours: z.number().int().positive() })
        .strict()
        .optional(),
    source,
})
    .strict();
export const reussiteSchema = z.object({ texte: texte, ancres: z.array(texte), source }).strict();
export const competenceSchema = z
    .object({
    code: z.enum(CODES_COMPETENCE),
    pfeq: z.enum(['C1', 'C2', 'C3']).optional(),
    etat: z.enum(ETATS_COMPETENCE),
    phrase: texte,
    /** Les `Constat.id` qui la fondent. */
    constats: z.array(texte),
})
    .strict();
export const retroactionSchema = z
    .object({
    version: z.literal(VERSION_RETROACTION),
    travail: z
        .object({
        type: z.enum(TYPES_TRAVAIL_RETROACTION),
        ref: z
            .object({
            exerciceCode: z.string().optional(),
            copieId: z.string().optional(),
            texteId: z.string().optional(),
            tentativeId: z.string().optional(),
        })
            .strict(),
        matiere: z.enum(['maths', 'francais']),
        /** Le titre du travail, tel que l'élève le connaît (« Du balcon au journal du quartier »). */
        titre: z.string().optional(),
        niveau: texte,
        date: texte,
        consigne: z
            .object({
            contexte: z.string().optional(),
            questions: z.array(z.object({ id: texte, texte: z.string() }).strict()).optional(),
        })
            .strict(),
    })
        .strict(),
    unites: z.array(uniteSchema),
    /** Français : ce qui se COMPTE, par le code (« compter n'est pas juger », spec § 7.6). */
    mesures: z
        .object({
        mots: z.number().int().nonnegative(),
        phrases: z.number().int().nonnegative(),
        paragraphes: z.number().int().nonnegative(),
        erreursLanguePour100Mots: z.number().nonnegative(),
        contraintes: z.array(z.object({ code: texte, tenue: z.boolean() }).strict()),
    })
        .strict()
        .optional(),
    verdict: z
        .object({
        global: verdict,
        titre: texte,
        /** Les unités `infere` non confirmées et `bloc: identite` ne comptent JAMAIS dans `sur`. */
        note: z
            .object({ obtenu: z.number().nonnegative(), sur: z.number().nonnegative() })
            .strict()
            .optional(),
    })
        .strict(),
    resume: z.string().optional(),
    reussites: z.array(reussiteSchema),
    constats: z.array(constatSchema),
    competences: z.array(competenceSchema).optional(),
    /** La mini-leçon : choisie par le code (fréquence × poids de la notion). */
    lecon: z
        .object({
        erreurCode: texte,
        fiche: z.object({ code: texte, ancre: texte }).strict(),
        capsule: z.object({ code: texte }).strict().optional(),
        exemple: z
            .object({ source: z.enum(['fiche', 'erreur', 'cache']), ref: z.string() })
            .strict()
            .optional(),
    })
        .strict()
        .optional(),
    fil: z
        .object({
        notion: texte,
        palier: texte,
        reviennent: z.array(z
            .object({
            code: texte,
            fois: z.number().int().positive(),
            fenetreJours: z.number().int().positive(),
        })
            .strict()),
    })
        .strict()
        .optional(),
    plan: z
        .object({
        prochainPas: z.object({ texte: texte, action: lienSchema }).strict().nullable(),
        etapes: z.array(z
            .object({ texte: texte, constat: z.string().optional(), action: lienSchema.optional() })
            .strict()),
    })
        .strict(),
    /**
     * Français (P3-d) : les RÉÉCRITURES GUIDÉES que l'élève a proposées, dans
     * l'ordre, et ce que le code en a dit — sans modèle (`a_juger` : hors des
     * formes connues, jugée plus tard). Gardées ici : aucune colonne de plus.
     */
    reecrituresEleve: z
        .array(z
        .object({
        constat: texte,
        texte: z.string(),
        issue: z.enum(['acceptee', 'presque', 'a_revoir', 'a_juger']),
        indice: z.string().optional(),
        le: texte,
    })
        .strict())
        .max(100)
        .optional(),
    /** Par chemin de champ (« resume », « constats.k1.explication ») : qui l'a écrit. */
    provenance: z.record(source),
    cout: z
        .object({
        credits: z.number().int().nonnegative(),
        jetons: z
            .object({
            entree: z.number().int().nonnegative(),
            cache: z.number().int().nonnegative(),
            sortie: z.number().int().nonnegative(),
            raisonnement: z.number().int().nonnegative().optional(),
        })
            .strict()
            .optional(),
    })
        .strict(),
})
    .strict();
/**
 * L'ORDRE DE LECTURE, commun à l'écran natif et au gabarit HTML : c'est lui
 * qui garantit que le PDF dit la même chose que l'écran.
 */
export const ORDRE_SECTIONS_RETROACTION = [
    'verdict',
    'reussites',
    'la_chose',
    'lecon',
    'autres_constats',
    'competences',
    'fil',
    'plan',
];
/** Le constat principal : celui que le code a marqué, sinon la première erreur. */
export function constatPrincipal(r) {
    return (r.constats.find((c) => c.principal) ?? r.constats.find((c) => c.nature === 'erreur') ?? null);
}
//# sourceMappingURL=retroaction.js.map